import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin } from 'lucide-react';
import { Restaurant } from '../../lib/types';
interface RestaurantCardProps {
  restaurant: Restaurant;
  zoneName?: string;
  tags?: string[];
}
export function RestaurantCard({
  restaurant,
  zoneName,
  tags = []
}: RestaurantCardProps) {
  const primary = restaurant.primary_color || '#C9A34E';
  const secondary = restaurant.secondary_color || '#FFFFFF';
  const accent = restaurant.accent_color || primary;
  return <Link to={`/r/${restaurant.slug}`} className="block group" data-id="element-268">
      <div className="relative h-[300px] rounded-2xl overflow-hidden border border-white/5 bg-[#141414] transition-shadow duration-300 hover:shadow-[0_0_24px_rgba(201,163,78,0.12)]" data-id="element-269">
        {/* Cover Image — uses cover_url, or a vibrant fallback */}
        <img src={restaurant.cover_url || 'https://cdn.magicpatterns.com/uploads/9aYwqfVJGQEzKkCYeBBzcT/MX.jpg'} alt={restaurant.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" decoding="async" data-id="element-270" />

        {/* Dark gradient overlay — stronger at bottom for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-black/10" data-id="element-271" />

        {/* Colored accent glow at bottom — uses restaurant's primary color */}
        <div className="absolute bottom-0 left-0 right-0 h-32 opacity-20 pointer-events-none" style={{
        background: `linear-gradient(to top, ${primary}40, transparent)`
      }} data-id="element-272" />

        {/* Top badges */}
        <div className="absolute top-3 left-3 right-3 flex items-start justify-between z-10" data-id="element-273">
          {restaurant.is_featured ? <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{
          backgroundColor: primary,
          color: '#0B0B0B'
        }} data-id="element-274">
              ★ Destacado
            </span> : <span data-id="element-275" />}

          {!restaurant.is_open ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-500/90 text-white" data-id="element-276">
              Cerrado
            </span> : <span className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/60" data-id="element-277">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" data-id="element-278" />
              <span className="text-[10px] font-medium text-white/80" data-id="element-279">
                Abierto
              </span>
            </span>}
        </div>

        {/* Bottom content */}
        <div className="absolute bottom-0 left-0 right-0 p-4 z-10" data-id="element-280">
          <div className="flex items-end gap-3" data-id="element-281">
            {/* Logo */}
            <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0 border-2" style={{
            borderColor: `${primary}60`,
            backgroundColor: '#0B0B0B'
          }} data-id="element-282">
              {restaurant.logo_url ? <img src={restaurant.logo_url} alt={`${restaurant.name} logo`} className="w-full h-full object-cover" loading="lazy" data-id="element-283" /> : <div className="w-full h-full flex items-center justify-center font-bold text-lg" style={{
              backgroundColor: primary,
              color: secondary
            }} data-id="element-284">
                  {restaurant.name.charAt(0)}
                </div>}
            </div>

            {/* Text content — name always white for readability */}
            <div className="flex-1 min-w-0" data-id="element-285">
              <h3 className="font-bold text-lg leading-tight line-clamp-1 mb-0.5 text-white" data-id="element-286">
                {restaurant.name}
              </h3>
              <p className="text-white/60 text-xs line-clamp-1" data-id="element-287">
                {restaurant.description_short || 'Deliciosa comida preparada con amor.'}
              </p>
            </div>
          </div>

          {/* Meta row */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10" data-id="element-288">
            <div className="flex items-center gap-3" data-id="element-289">
              <div className="flex items-center gap-1 text-white/50" data-id="element-290">
                <MapPin className="w-3 h-3" data-id="element-291" />
                <span className="text-[11px] font-medium" data-id="element-292">
                  {zoneName || restaurant.address_text || 'Los Santos'}
                </span>
              </div>
              <div className="flex items-center gap-1" data-id="element-293">
                <Star className="w-3 h-3" style={{
                color: primary,
                fill: primary
              }} data-id="element-294" />
                <span className="text-[11px] font-semibold" style={{
                color: primary
              }} data-id="element-295">
                  4.8
                </span>
              </div>
            </div>

            <span className="text-xs font-bold transition-colors" style={{
            color: accent
          }} data-id="element-296">
              {restaurant.is_open ? 'Ordenar →' : 'Ver menú →'}
            </span>
          </div>
        </div>
      </div>
    </Link>;
}